package decoders

import bitmage.hex
import bitmage.untilIndex
import htmlEscape
import veryLargeDataFieldThreshold

interface ByteWitchResult {

    enum class Colour {
        BPLIST, PROTOBUF, OPACK, NSARCHIVE, ASN1, GENERIC, NEUTRAL, PLAIN
    }

    val colour: ByteWitchResult.Colour

    fun renderHTML(): String

    fun wrapIfSameColour(subresult: ByteWitchResult): String {
        return if(subresult.colour == Colour.PLAIN || subresult.colour != colour)
            subresult.renderHTML()
        else {
            "<div class=\"bwvalue data\">${subresult.renderHTML()}</div>"
        }
    }

    fun wrapIfSameColour(subresult: ByteWitchResult?, fallback: String, rangeTags: String): String {
        return if(subresult == null)
            bwvalue(fallback, rangeTags, data = true)
        else
            wrapIfSameColour(subresult)
    }

    fun wrapIfSameColour(subresult: ByteWitchResult?, data: ByteArray, rangeTags: String): String {
        val fallback = if(data.size < veryLargeDataFieldThreshold) "0x${data.hex()}" else "long data (${humanReadableByteCount(data.size)}), first 32B: 0x${data.untilIndex(32).hex()}"
        return wrapIfSameColour(subresult, fallback, rangeTags)
    }

    fun rangeTagsFor(start: Int, end: Int) = "data-start=\"$start\" data-end=\"$end\""
    fun relativeRangeTags(start: Int, length: Int) : String {
        check(sourceByteRange != null) { "attempting use of relativeRangeTagsFor with null sourceByteRange" }
        return rangeTagsFor(sourceByteRange!!.first+start, sourceByteRange!!.first+start+length)
    }

    fun humanReadableByteCount(len: ULong): String {
        val kb = 1024UL
        val mb = kb * 1024UL

        return when(len) {
            in 0UL..(5U*kb) -> "$len B"
            in 5U*kb..5U*mb -> "<span title=\"$len B\">${len/kb} KiB</span>"
            else -> "<span title=\"$len B\">${len/mb} MiB</span>"
        }
    }
    fun humanReadableByteCount(len: Int) = humanReadableByteCount(len.toULong())

    val sourceByteRange: Pair<Int,Int>?

    val byteRangeDataTags: String
        get() = if(sourceByteRange == null || sourceByteRange!!.first < 0) "" else rangeTagsFor(sourceByteRange!!.first, sourceByteRange!!.second)
}

class MultiPartialDecode(val parts: List<Pair<ByteWitchResult?, BWRangeTaggedData?>>, override val sourceByteRange: Pair<Int, Int>): ByteWitchResult {
    override val colour = ByteWitchResult.Colour.NEUTRAL

    override fun renderHTML(): String {
        val items = parts.mapNotNull {
            when {
                it.first != null -> wrapIfSameColour(it.first!!)
                it.second != null && it.second!!.data.isNotEmpty() -> bwvalue("0x${it.second!!.data.hex()}",  rangeTagsFor(it.second!!.start, it.second!!.start + it.second!!.data.size), data = true)
                else -> null
            }
        }

        return "<div class=\"roundbox neutral largecollection\" $byteRangeDataTags>${items.joinToString(" ")}</div>"
    }
}

open class BWGenericSequence(val elements: List<ByteWitchResult>, override val sourceByteRange: Pair<Int, Int>, val vertical: Boolean = false) : ByteWitchResult {
    override val colour = ByteWitchResult.Colour.GENERIC
    override fun renderHTML(): String {
        val large = if(vertical) " largecollection" else ""
        return if(elements.size == 1)
            elements.first().renderHTML()
        else
            "<div class=\"generic roundbox$large\" $byteRangeDataTags>${elements.joinToString("") { wrapIfSameColour(it, "", rangeTagsFor(it.sourceByteRange!!.first, it.sourceByteRange!!.second)) }}</div>"
    }
}

class BWGenericData(val data: ByteArray, override val sourceByteRange: Pair<Int, Int>): ByteWitchResult {
    override val colour = ByteWitchResult.Colour.PLAIN
    override fun renderHTML(): String {
        val quickDecode = ByteWitch.quickDecode(data, sourceByteRange.first)
        return wrapIfSameColour(quickDecode, data, rangeTagsFor(sourceByteRange.first, sourceByteRange.second))
    }
}

class BWStringCollection(val elements: List<BWString>, override val sourceByteRange: Pair<Int, Int>) : ByteWitchResult {
    override val colour = ByteWitchResult.Colour.NEUTRAL
    override fun renderHTML() = "<div class=\"roundbox neutral\">" + elements.joinToString(" ") { it.renderHTML() } + "</div>"
}

open class BWString(val string: String, override val sourceByteRange: Pair<Int, Int>) : ByteWitchResult {
    override val colour = ByteWitchResult.Colour.PLAIN
    override fun renderHTML() = "<div class=\"bwvalue stringlit\" $byteRangeDataTags>${htmlEscape(string)}</div>"
}

class BWLinkedString(string: String, private val url: String, sourceByteRange: Pair<Int, Int>): BWString(string, sourceByteRange) {
    override val colour = ByteWitchResult.Colour.PLAIN
    override fun renderHTML() = "<div class=\"bwvalue stringlit\" $byteRangeDataTags>${htmlEscape(string)} <a href=\"${url}\" target=\"_blank\">(info)</a></div>"
}

class BWAnnotatedData(val annotationHTML: String, val data: ByteArray, override val sourceByteRange: Pair<Int, Int>) : ByteWitchResult {
    override val colour = ByteWitchResult.Colour.PLAIN
    override fun renderHTML(): String {
        val dataRendering = if(data.size > veryLargeDataFieldThreshold)
            "long data (${humanReadableByteCount(data.size)}), first 32B: 0x${data.untilIndex(32).hex()}"
        else
            "0x${data.hex()}"
        return "<div class=\"bwvalue data\" $byteRangeDataTags>$annotationHTML $dataRendering</div>"
    }
}

class BWRangeTaggedData(val data: ByteArray, val start: Int)
class BWRangeTaggedInt(val value: Int, val start: Int, val length: Int)

fun bwvalue(content: String, rangeTags: String, data: Boolean = false) = "<div class=\"bwvalue${if(data) " data" else ""}\" $rangeTags>$content</div>"